import { Button, Input, Modal, Notice, Select, Textarea } from '@bennows/boows-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiFailure } from '../../lib/api';
import { useToast } from '../../lib/toasts';
import { useTenantActions, type IndustryProfile, type TenantDetail } from './useTenant';
import { INDUSTRY_PROFILES } from './useTenants';

export type TenantAction = 'suspend' | 'unsuspend' | 'industry';

const FORM_ID = 'tenant-action';
/** As the API: a reason of at least three characters, at most 500 (REQ-140-005). */
const REASON_MIN = 3;
const REASON_MAX = 500;

/** Message keys for the problem answers of the tenant writes. */
const FAILURES: Record<string, string> = {
  'tenant-already-suspended': 'tenantActions.alreadySuspended',
  'tenant-not-suspended': 'tenantActions.notSuspended',
  'not-found': 'tenant.notFoundText',
  network: 'common.errorNetwork',
};

/**
 * One intervention on a tenant with its reason (REQ-140-021, REQ-140-022).
 * Suspending also asks for the company name typed out (REQ-140-024).
 */
export function TenantActionDialog({ tenant, action, onClose }: { readonly tenant: TenantDetail; readonly action: TenantAction; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const actions = useTenantActions(tenant.id);
  const [reason, setReason] = useState('');
  const [typedName, setTypedName] = useState('');
  const [profile, setProfile] = useState<IndustryProfile>(tenant.industry_profile);
  const [submitted, setSubmitted] = useState(false);
  const mutation = action === 'suspend' ? actions.suspend : action === 'unsuspend' ? actions.unsuspend : actions.changeIndustry;

  const reasonMissing = reason.trim().length < REASON_MIN;
  const nameMismatch = action === 'suspend' && typedName.trim() !== tenant.name;
  const unchanged = action === 'industry' && profile === tenant.industry_profile;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (reasonMissing || nameMismatch) return;
    if (unchanged) return onClose();
    const done = { onSuccess: () => (toast(t(`tenantActions.${action}.done`, { name: tenant.name })), onClose()) };
    if (action === 'industry') actions.changeIndustry.mutate({ profile, reason: reason.trim() }, done);
    else (action === 'suspend' ? actions.suspend : actions.unsuspend).mutate(reason.trim(), done);
  };

  const failure = mutation.error instanceof ApiFailure ? (FAILURES[mutation.error.slug] ?? 'common.errorUnexpected') : mutation.error ? 'common.errorUnexpected' : null;
  return (
    <Modal
      open
      onClose={onClose}
      title={t(`tenantActions.${action}.title`)}
      description={tenant.name}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={FORM_ID} variant={action === 'suspend' ? 'danger' : 'primary'} loading={mutation.isPending}>
            {t(`tenantActions.${action}.submit`)}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="ui-console-form" onSubmit={onSubmit} noValidate>
        {action === 'suspend' ? (
          <Notice tone="warning" role="none">
            {t('tenantActions.suspend.effects')}
          </Notice>
        ) : null}
        {action === 'unsuspend' ? <p className="ui-console-muted">{t('tenantActions.unsuspend.effects')}</p> : null}
        {action === 'industry' ? (
          <Select
            label={t('tenantActions.industry.profile')}
            options={INDUSTRY_PROFILES.map((id) => ({ id, label: t(`industryProfiles.${id}`) }))}
            value={profile}
            onChange={(id) => setProfile(id as IndustryProfile)}
            help={t('tenantActions.industry.effects')}
          />
        ) : null}
        <Textarea
          label={t('tenantActions.reason')}
          value={reason}
          onChange={setReason}
          rows={3}
          maxLength={REASON_MAX}
          required
          invalid={submitted && reasonMissing}
          help={submitted && reasonMissing ? t('tenantActions.reasonMissing') : t('tenantActions.reasonHelp')}
        />
        {action === 'suspend' ? (
          <Input
            label={t('tenantActions.suspend.confirmName', { name: tenant.name })}
            value={typedName}
            onChange={setTypedName}
            required
            invalid={submitted && nameMismatch}
            help={submitted && nameMismatch ? t('tenantActions.suspend.nameMismatch') : undefined}
          />
        ) : null}
        {failure ? (
          <p className="ui-auth__message" role="alert">
            {t(failure)}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
